import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Stack,
  Collapse,
  IconButton,
  Link,
  Button,
  CircularProgress,
  Fade,
} from "@mui/material";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useAuth } from "../../context/AuthContext";
import { useAppConfig } from "../../context/AppConfigContext";
import api from "../../services/api";
import { useSnackbarAlert } from "../../hooks/useSnackbarAlert";

const LoyaltyPointsCard = () => {
  const { user, isAuthenticated } = useAuth();

  const { config } = useAppConfig();
  const snackbar = useSnackbarAlert();
  const [memberData, setMemberData] = useState(null);
  const [loadingMember, setLoadingMember] = useState(true);
  const [linking, setLinking] = useState(false);
  const [showRewards, setShowRewards] = useState(false);

  const rewardTiers = config?.loyalty?.rewardTiers || [];
  const loading = loadingMember || linking;

  useEffect(() => {
    if (!isAuthenticated || !user) return null;

    if (user?.profile?.email) {
      fetchMember();
    }
  }, [user]);

  const fetchMember = async () => {
    try {
      setLoadingMember(true);
      const res = await api.get(
        `/admin/digitalLoyalty/memberByEmail?email=${user.profile.email}`
      );
      if (res.data) {
        setMemberData(res.data);
      } else {
        setMemberData(null);
      }
    } catch (err) {
      console.error("Error loading loyalty member:", err);
      setMemberData(null);
    } finally {
      setLoadingMember(false);
    }
  };

  const handleLinkAccount = async () => {
    try {
      setLinking(true);
      setTimeout(async () => {
        await api.post("/admin/digitalLoyalty/manualLink", {
          firstName: user.profile.firstName,
          lastName: user.profile.lastName,
          email: user.profile.email,
        });

        snackbar.success("Account Linked Successfully 🎉");
        await fetchMember();
        setLinking(false);
      }, 2000);
    } catch (error) {
      console.error("Error linking loyalty account:", error);
      snackbar.error("Failed to link account. Please try again.");
      setLinking(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={5}>
        <CircularProgress size={28} thickness={4} color="inherit" />
      </Box>
    );
  }

  const points = memberData?.points ?? 0;
  const sortedTiers = [...rewardTiers].sort((a, b) => a.points - b.points);
  const nextTier = sortedTiers.find((tier) => points < tier.points);
  const maxTier = [...sortedTiers]
    .reverse()
    .find((tier) => points >= tier.points);
  const pointsToNext = nextTier ? nextTier.points - points : null;

  return (
    <Fade in={!loading} timeout={500}>
      <Box>
        {!memberData ? (
          <Box textAlign="center" py={5}>
            <Typography variant="h6" gutterBottom>
              This account is not registered with our loyalty system
            </Typography>
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
              onClick={handleLinkAccount}
              disabled={linking}>
              {linking ? "Linking..." : "Link My Account"}
            </Button>
          </Box>
        ) : (
          <>
            <Stack direction="row" alignItems="center" spacing={2}>
              <CardGiftcardIcon color="action" sx={{ fontSize: 36 }} />
              <Box width="100%">
                <Typography fontWeight="bold">
                  You have {points} {points === 1 ? "point" : "points"}
                </Typography>
                {maxTier ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}>
                    You qualify for: <strong>{maxTier.reward}</strong>
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}>
                    Earn points to unlock rewards 🎁
                  </Typography>
                )}

                {nextTier && (
                  <>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1.5, display: "block" }}>
                      <Box display="flex" alignItems="center" mt={1.5} gap={1}>
                        <Typography variant="caption" color="text.secondary">
                          <strong>{pointsToNext}</strong> more points to unlock:
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: "#FD7762",
                            color: "#fff",
                            px: 1.5,
                            py: 0.3,
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            borderRadius: "12px",
                            whiteSpace: "nowrap",
                          }}>
                          {nextTier.reward}
                        </Box>
                      </Box>
                    </Typography>

                    <Box mt={1}>
                      <Typography
                        variant="body2"
                        sx={{ color: "#888", mb: 0.5 }}>
                        Progress to <strong>{nextTier.reward}</strong>
                      </Typography>
                      <Box
                        sx={{
                          height: 10,
                          backgroundColor: "#f0f0f0",
                          borderRadius: 5,
                          overflow: "hidden",
                        }}>
                        <Box
                          sx={{
                            width: `${(points / nextTier.points) * 100}%`,
                            maxWidth: "100%",
                            height: "100%",
                            backgroundColor: "#FD7762",
                            transition: "width 0.3s ease",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ mt: 0.5, display: "block", color: "#777" }}>
                        {points} / {nextTier.points} points
                      </Typography>
                    </Box>
                  </>
                )}

                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  mt={1.5}>
                  <Link
                    component="button"
                    underline="hover"
                    onClick={() => setShowRewards(!showRewards)}
                    sx={{
                      fontSize: "0.85rem",
                      color: "#FD7762",
                      fontWeight: 500,
                    }}>
                    {showRewards
                      ? "Hide reward system"
                      : "View full reward system"}
                  </Link>
                  <IconButton
                    size="small"
                    onClick={() => setShowRewards(!showRewards)}>
                    {showRewards ? (
                      <ExpandLessIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </Stack>
              </Box>
            </Stack>

            <Collapse in={showRewards}>
              <Box mt={2} ml={6}>
                {sortedTiers.map((tier) => (
                  <Typography
                    key={tier.points}
                    variant="body2"
                    sx={{ mb: 0.5, color: "#555" }}>
                    • <strong>{tier.points} points</strong> – {tier.reward}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    </Fade>
  );
};

export default LoyaltyPointsCard;
